import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from "@mui/material";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import VantageListHead from "../../../component/ListHead/SignalList";
import {
  TABLE_HEAD_SIGNALS_CURRENT as TABLE_HEAD_FULL,
  applySortFilter,
  getComparator,
  hashInvestment,
} from "../../../component/Methods";

/**
 * Build row objects matching Signals → current list shape (ticker, weight %, L/S, asset type, optional news).
 */
export function buildSignalWeightsRows(weights_keys, weights_values, newsData = {}) {
  if (!weights_keys || !weights_values) return [];
  const keys = Object.keys(weights_keys);
  return keys.map((key) => {
    const investment = weights_keys[key]?.investment;
    const symbol = weights_keys[key]?.symbol;
    const stock = weights_keys[key]?.stock;
    const name = stock ? `${symbol}/${stock}` : symbol;
    const newsItems = newsData[symbol?.toUpperCase()];
    return {
      key,
      investment,
      name: name?.toUpperCase() || "",
      value: (weights_values[key] * 100)?.toFixed(4),
      long_short: weights_values[key] >= 0 ? "L" : "S",
      stock,
      news: newsItems,
      assetType: hashInvestment(investment) || "Equity",
    };
  });
}

const defaultTableContainerSx = {
  flex: 1,
  overflowX: "hidden",
  overflowY: "auto",
};

/**
 * Tabular weights view (same columns as SignalCurrentList: Ticker, Weighting %, L/S, Asset Type, optional News).
 */
export function SignalWeightsTable({
  rows,
  order,
  orderBy,
  onRequestSort,
  showNewsColumn = true,
  hasFetchedNews = false,
  onNewsClick,
  tableContainerSx,
  stickyHeader = true,
  "aria-label": ariaLabel = "signal weights",
}) {
  const theme = useTheme();

  const headLabel = useMemo(
    () =>
      showNewsColumn
        ? TABLE_HEAD_FULL
        : TABLE_HEAD_FULL.filter((h) => h.id !== "news"),
    [showNewsColumn]
  );

  const containerSx = tableContainerSx ?? defaultTableContainerSx;

  return (
    <TableContainer sx={containerSx}>
      <Table size="small" stickyHeader={stickyHeader} aria-label={ariaLabel}>
        <VantageListHead
          order={order}
          orderBy={orderBy}
          headLabel={headLabel}
          onRequestSort={onRequestSort}
          noStatus
          customizeFont={12}
        />
        <TableBody>
          {rows?.map((row) => {
            const ticker = row.name.split("/")[0];
            return (
              <TableRow
                key={row.key}
                sx={{
                  overflow: "auto",
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },
                  "& > .MuiTableCell-root": {
                    paddingTop: "1px",
                    paddingBottom: "0px !important",
                    lineHeight: "1.2",
                  },
                }}
              >
                <TableCell
                  align="left"
                  sx={{
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    cursor: "pointer",
                  }}
                >
                  {row.name}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                >
                  {row.value}%
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontSize: "12px",
                    paddingLeft: "10px",
                    color: theme.palette.text.primary,
                    borderLeft: "none",
                    borderRight: "none",
                    height: "21.5px",
                  }}
                >
                  {row.long_short}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontSize: "12px",
                    paddingLeft: "10px",
                    color: theme.palette.text.primary,
                    borderLeft: "none",
                    borderRight: "none",
                    height: "21.5px",
                  }}
                >
                  {row.assetType}
                </TableCell>
                {showNewsColumn ? (
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      color: theme.palette.text.primary,
                      borderLeft: "none",
                      borderRight: "none",
                    }}
                  >
                    {hasFetchedNews && row.news && row.news.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                          cursor: "pointer",
                          height: "20px",
                          maxHeight: "20px",
                        }}
                        onClick={() => onNewsClick?.(row.news, ticker)}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            color: theme.palette.primary.main,
                            backgroundColor: theme.palette.primary.light + "20",
                            borderRadius: "4px",
                            padding: "2px",
                            minWidth: "16px",
                            width: "16px",
                            height: "16px",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.light + "40",
                              transform: "scale(1.05)",
                              boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
                            },
                            transition: "all 0.2s ease-in-out",
                            "& .MuiSvgIcon-root": {
                              fontSize: "12px",
                            },
                          }}
                        >
                          <NewspaperIcon fontSize="inherit" />
                        </IconButton>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.error.main,
                            color: "white",
                            fontSize: "11px",
                            height: "12px",
                            minWidth: "12px",
                            borderRadius: "6px",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 2px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                          }}
                        >
                          {row.news.length}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Same table as above with local sort state; pass raw `weights_keys` / `weights_values` (e.g. historical signal set entry).
 */
export function SignalWeightsTableFromPayload({
  weights_keys,
  weights_values,
  newsData = {},
  showNewsColumn = false,
  hasFetchedNews = false,
  onNewsClick,
  tableContainerSx,
}) {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("value");

  const baseRows = useMemo(
    () => buildSignalWeightsRows(weights_keys, weights_values, newsData),
    [weights_keys, weights_values, newsData]
  );

  const rows = useMemo(
    () => applySortFilter(baseRows, getComparator(order, orderBy)),
    [baseRows, order, orderBy]
  );

  const handleRequestSort = useCallback((event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }, [order, orderBy]);

  return (
    <SignalWeightsTable
      rows={rows}
      order={order}
      orderBy={orderBy}
      onRequestSort={handleRequestSort}
      showNewsColumn={showNewsColumn}
      hasFetchedNews={hasFetchedNews}
      onNewsClick={onNewsClick}
      tableContainerSx={tableContainerSx}
    />
  );
}

export default SignalWeightsTable;
